import { MapPinIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect, useRef, FC } from "react";
import useFetch from "hooks/useFetch";
import config from "config.json";

interface Hotel {
  codigo: string;
  descripcion: string;
  ciudad: string;
  direccion: string;
  precio_por_noche: string;
  cant_lugares: number;
  banios_compartidos: number;
  banios_habitaciones: number;
}

interface Props {
  onChange?: (value: string) => void;
  className?: string;
  defaultValue?: string;
  headingText?: string;
}

const LocationInput: FC<Props> = ({
  onChange = () => { },
  className = "",
  defaultValue = "",
  headingText = "Busca tu destino",
}) => {
  const url = config.backend_url;
  const [value, setValue] = useState(defaultValue);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [searchResults, setSearchResults] = useState<Hotel[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, error, isLoading, fetchData } = useFetch<Hotel[]>(
    'GET',
    `${url}/setia/hoteles`,
    { 'Content-Type': 'application/json' }
  );

  useEffect(() => {
    if (!data) {
      fetchData(); // Llamada para obtener los datos de los hoteles al montar el componente
    }
  }, []); // Solo se ejecuta una vez al montar el componente

  useEffect(() => {
    if (data) {
      const response = data as any;
      setHotels(response.hoteles);
    }
  }, [data]); // Agregar 'data' al arreglo de dependencias para que se vuelva a filtrar cuando los datos cambien

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const searchLocations = (searchTerm: string) => {
    const filteredResults = hotels.filter(hotel =>
      hotel.descripcion.toLowerCase().includes(value.toLowerCase()) ||
      hotel.ciudad.toLowerCase().includes(value.toLowerCase())
    );
    setSearchResults(filteredResults);
  };

  useEffect(() => {
    if (value) {
      searchLocations(value);
    }
  }, [value, hotels]); // Agregar 'hotels' al arreglo de dependencias para que se vuelva a filtrar cuando los datos de los hoteles cambien

  const handleSelectLocation = (item: string) => {
    setValue(item);
    onChange(item);

    const selectedHotel = hotels.find(hotel => hotel.descripcion === item);
    if (selectedHotel) {
      sessionStorage.setItem('selected_hotel', selectedHotel.codigo);
      console.log(`Selected hotel code: ${selectedHotel.codigo}`);
    }
  };

  const renderSearchValues = ({
    heading,
    items,
  }: {
    heading: string;
    items: Hotel[]; // Cambiado a Hotel[] en lugar de string[] para manejar ambos valores
  }) => {
    return (
      <>
        <p className="block font-semibold text-base">
          {heading}
        </p>
        <div className="mt-3">
          {items.map((hotel) => {
            return (
              <div
                className="py-2 mb-1 flex items-center space-x-3 text-sm"
                onClick={() => handleSelectLocation(hotel.descripcion)} // Usamos la descripción para seleccionar el hotel
                key={hotel.codigo}
              >
                <MapPinIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                <span className="font-medium">{hotel.descripcion}</span>
                <span className="text-neutral-500 dark:text-neutral-400">{hotel.ciudad}</span> {/* Muestra la ciudad */}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className={`${className}`} ref={containerRef}>
      <div className="p-5">
        <span className="block font-semibold text-xl sm:text-2xl">
          {headingText}
        </span>
        <div className="relative mt-5">
          <input
            className={`block w-full bg-transparent border px-4 py-3 pr-12 border-neutral-900 dark:border-neutral-200 rounded-xl focus:ring-0 focus:outline-none text-base leading-none placeholder-neutral-500 dark:placeholder-neutral-300 truncate font-bold placeholder:truncate`}
            placeholder={"Ingresa tu búsqueda"}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            ref={inputRef}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <MagnifyingGlassIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-400" />
          </span>
        </div>
        <div className="mt-7 h-36 overflow-auto">
          {searchResults.length > 0 ? (
            renderSearchValues({
              heading: "Resultado de búsqueda",
              items: searchResults, // Pasamos los objetos completos, no solo las descripciones
            })
          ) : (
            renderSearchValues({
              heading: "Todos los resultados",
              items: hotels, // Pasamos los objetos completos, no solo las descripciones
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationInput;